/**
 * FloatingContactButton Component
 * 
 * Fixed position floating button providing contact/support access.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Mail } from 'lucide-react';

function FloatingContactButton() {
  const [showDetails, setShowDetails] = useState(false);
  const contactRef = React.useRef(null);
  
  // Hide Emergent badge on authenticated pages
  useEffect(() => {
    const hideEmergentBadge = () => {
      // Target iframe or div that contains "Emergent" text and is fixed position
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (iframe.src && iframe.src.includes('emergent')) {
          iframe.style.display = 'none';
        }
      });
      
      // Target any fixed position elements in bottom right
      const allElements = document.querySelectorAll('*');
      allElements.forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.position === 'fixed' && 
            el.textContent.toLowerCase().includes('emergent')) {
          el.style.display = 'none';
        }
      });
    };
    
    // Run immediately and also after a delay to catch late-loading badges
    hideEmergentBadge();
    const interval = setInterval(hideEmergentBadge, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Click outside to close
  useEffect(() => {
    if (!showDetails) return;
    
    const handleClickOutside = (event) => {
      if (contactRef.current && !contactRef.current.contains(event.target)) {
        setShowDetails(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDetails]);
  
  return (
    <div ref={contactRef} className="fixed bottom-6 right-6 z-50">
      {showDetails && (
        <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-xl border-2 border-orange-500 p-4 w-64">
          <button 
            onClick={() => setShowDetails(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
          <h3 className="font-bold text-gray-800 mb-3">Contact Us</h3>
          <div className="space-y-2">
            <a 
              href="tel:469-232-7678"
              className="flex items-center gap-2 text-gray-700 hover:text-orange-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm">469-232-7678</span>
            </a>
            <a 
              href="mailto:admin@caterlog.net"
              className="flex items-center gap-2 text-gray-700 hover:text-orange-600 transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm">admin@caterlog.net</span>
            </a>
          </div>
        </div>
      )}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="bg-orange-600 hover:bg-orange-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
        title="Contact Us"
      >
        <Mail className="w-5 h-5" />
        <span className="font-medium">Contact</span>
      </button>
    </div>
  );
}



// Subscription Success Page Component - Must be defined before App component

export default FloatingContactButton;
